import React, { useCallback, memo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Instagram,
  Linkedin,
  Mail,
  ArrowUpRight,
  Phone,
  Loader2,
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import {
  EmailSubscriptionSchema,
  validateWithSchema,
} from '@/utils/validation';
import { csrfProtection } from '@/utils/csrf';
import {
  checkRateLimit,
  getTimeUntilRateLimitReset,
  RATE_LIMIT_KEYS,
} from '@/lib/utils/rateLimit';
import { trackNewsletterSignup } from '@/lib/utils/dataLayer';
import Logo from '@/components/common/Logo';
import BBBSeal from '@/components/seo/BBBSeal';
import TurnstileWidget from '@/components/common/TurnstileWidget';
import { useFormShield } from '@/hooks/useFormShield';

// Strong email validation regex
const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

// Legacy email validation function - now replaced by Zod schema
const _validateEmail = (email: string): boolean => {
  return EMAIL_REGEX.test(email.toLowerCase());
};

// SVG components memoized for better performance
const XIcon = memo(() => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
      fill="currentColor"
      stroke="none"
    />
  </svg>
));
XIcon.displayName = 'XIcon';

const TikTokIcon = memo(() => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"
      fill="currentColor"
      stroke="none"
    />
  </svg>
));
TikTokIcon.displayName = 'TikTokIcon';

const ThreadsIcon = memo(() => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    stroke="currentColor"
    aria-hidden="true"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" fill="currentColor" />
    <circle cx="12" cy="12" r="4" stroke="white" strokeWidth="2" />
    <path
      d="M12 8C15 8 16 10 16 12"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M16 12C16 14 15 16 12 16"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
));
ThreadsIcon.displayName = 'ThreadsIcon';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  // Honeypot + timing + Turnstile, verified in /api/newsletter/subscribe.
  const shield = useFormShield();

  // Ensure CSRF cookie exists for this session
  useEffect(() => {
    csrfProtection.setCSRFCookie();
  }, []);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value.trim());
      // Clear error/success messages on input change
      if (errorMessage) setErrorMessage('');
      if (successMessage) setSuccessMessage('');
    },
    [errorMessage, successMessage]
  );

  const handleSubscribe = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!email) {
        toast({
          title: 'Error',
          description: 'Please enter your email address',
          variant: 'destructive',
        });
        return;
      }

      // Build payload for Zod validation (not persisted to DB)
      const payload = {
        email,
        source: 'footer',
        csrf_token: csrfProtection.getCSRFCookie() || '',
        _hp: '', // honeypot must be empty to pass
      };

      // Run schema validation
      const result = validateWithSchema(EmailSubscriptionSchema, payload);

      if (!result.success) {
        toast({
          title: 'Invalid Email',
          description: 'Please enter a valid email address',
          variant: 'destructive',
        });
        return;
      }

      // Client-side rate limit: 3 signups/hour for newsletter
      const okClient = checkRateLimit(
        RATE_LIMIT_KEYS.EMAIL_SUBSCRIPTION,
        3,
        60 * 60 * 1000
      );
      if (!okClient) {
        const waitMs = getTimeUntilRateLimitReset(
          RATE_LIMIT_KEYS.EMAIL_SUBSCRIPTION
        );
        const waitSec = Math.ceil(waitMs / 1000);
        toast({
          title: 'Rate limited',
          description: `Please wait ${waitSec}s before subscribing again.`,
          variant: 'destructive',
        });
        return;
      }

      setIsSubmitting(true);

      try {
        const response = await fetch('/api/newsletter/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...shield.payload(),
            email: email.toLowerCase().trim(),
            source: 'footer',
            _hp: '',
          }),
        });
        const responseBody = (await response.json().catch(() => ({}))) as {
          already_subscribed?: boolean;
          pending?: boolean;
          email?: string;
          error?: string;
        };

        if (!response.ok) {
          // The token is spent either way, so retrying with it would fail
          // again. Hand the visitor a working second attempt.
          shield.resetToken();
          toast({
            title: 'Error',
            description:
              responseBody.error ||
              'Unable to subscribe. Please try again later.',
            variant: 'destructive',
          });
          setErrorMessage(
            responseBody.error || 'Unable to subscribe. Please try again later.'
          );
          return;
        }

        if (responseBody.already_subscribed) {
          setEmail('');
          setSuccessMessage("You're already on the list — nothing else to do.");
          toast({
            title: 'Already subscribed',
            description: "You're already on the list — nothing else to do.",
          });
          return;
        }

        if (!responseBody.pending) {
          // 200, but no pending subscription was created — the spam shield
          // discarded it. A bot must learn nothing from this, which is why the
          // server answers 200. A real visitor whose Turnstile token expired or
          // was already spent lands here too, and telling *them* to go watch
          // for a confirmation email that will never arrive is the worst
          // outcome this form has. So: no false promise, a fresh token, and an
          // attempt that actually works next time.
          shield.resetToken();
          setErrorMessage("That didn't go through. Please try once more.");
          toast({
            title: "That didn't go through",
            description: 'Please try once more.',
            variant: 'destructive',
          });
          return;
        }

        // Track newsletter signup in GTM dataLayer
        trackNewsletterSignup();

        // Deliberately no redirect. The next step is in their inbox, so the
        // message has to name the address and stay on screen while they go
        // look for it — bouncing them to a generic thank-you page would read
        // as "done" when they are one click short of subscribed.
        const sentTo = responseBody.email || email.toLowerCase().trim();
        setEmail('');
        setSuccessMessage(
          `Almost there — we sent a confirmation link to ${sentTo}. Click it and you're in.`
        );
        toast({
          title: 'Check your inbox',
          description: `We sent a confirmation link to ${sentTo}.`,
        });
      } catch (err) {
        console.error('Error:', err);
        toast({
          title: 'Error',
          description: 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, shield]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubscribe(e);
      }
    },
    [handleSubscribe]
  );

  // Success/Error message renders
  const renderFeedback = () => {
    if (successMessage) {
      return <p className="text-green-400 text-sm mt-2">{successMessage}</p>;
    }
    if (errorMessage) {
      return <p className="text-red-400 text-sm mt-2">{errorMessage}</p>;
    }
    return null;
  };

  return (
    <footer className="relative bg-black pt-16 pb-24 md:pb-16 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="glass-panel rounded-2xl p-8 md:p-12 backdrop-blur-md border border-white/10">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-4">
            {/* Logo and company info */}
            <div className="space-y-4">
              <Link
                to="/"
                className="flex items-center gap-2"
                aria-label="Elev8 Home"
              >
                <Logo className="h-9 w-auto" />
              </Link>
              <p className="text-white/70 text-sm mt-4">
                We help trade and service businesses book more jobs with AI
                agents, ads, automation, and websites.
              </p>
              <p className="text-white/60 text-sm mt-2">Based in Dracut, MA</p>
              <a
                href="tel:+19783192888"
                className="text-elev8-blue hover:text-elev8-blue/80 transition-colors text-sm block mt-2"
              >
                (978) 319-2888
              </a>
              <a
                href="https://share.google/9ZliopUdf4RK0xmEW"
                target="_blank"
                rel="noopener noreferrer"
                className="text-elev8-blue hover:text-elev8-blue/80 transition-colors text-sm block mt-2"
              >
                Find us on Google
              </a>
              <div className="flex space-x-3 pt-2">
                <SocialIcon
                  icon={<XIcon />}
                  href="https://x.com/elev8growthsol"
                  ariaLabel="X (Twitter)"
                />
                <SocialIcon
                  icon={<Instagram size={18} />}
                  href="https://www.instagram.com/elev8growthsol/"
                  ariaLabel="Instagram"
                />
                <SocialIcon
                  icon={<ThreadsIcon />}
                  href="https://www.threads.net/@elev8growthsol"
                  ariaLabel="Threads"
                />
                <SocialIcon
                  icon={<TikTokIcon />}
                  href="https://www.tiktok.com/@elev8_growthsol"
                  ariaLabel="TikTok"
                />
                <SocialIcon
                  icon={<Linkedin size={18} />}
                  href="https://www.linkedin.com/company/elev8-growth-solutions"
                  ariaLabel="LinkedIn"
                />
                <SocialIcon
                  icon={<Mail size={18} />}
                  href="mailto:info@elev8growth.solutions"
                  ariaLabel="Email"
                />
                <SocialIcon
                  icon={<Phone size={18} />}
                  href="tel:+19783192888"
                  ariaLabel="Phone"
                />
              </div>

              {/* Better Business Bureau accredited-business seal */}
              <BBBSeal className="bbb-seal pt-2" />
            </div>

            {/* Services */}
            <div>
              <h3 className="text-white font-bold text-lg mb-4">Services</h3>
              <ul className="space-y-2">
                <FooterLink href="/services/agents">AI Agents</FooterLink>
                <FooterLink href="/voicemail">AI Voicemail</FooterLink>
                <FooterLink href="/demo/ai-voicemail">
                  AI Voicemail Demo
                </FooterLink>
                <FooterLink href="/services/ads">Advertising</FooterLink>
                <FooterLink href="/services/automation">Automation</FooterLink>
                <FooterLink href="/web-design">Websites</FooterLink>
                <FooterLink href="/review-stands">Review Stands</FooterLink>
                <FooterLink href="/services/google-ads-for-contractors">
                  Google Ads for Contractors
                </FooterLink>
                <FooterLink href="/industries">Industries</FooterLink>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-white font-bold text-lg mb-4">Company</h3>
              <ul className="space-y-2">
                <FooterLink href="/about">About Us</FooterLink>
                <FooterLink href="/team">Team</FooterLink>
                <FooterLink href="/locations">Service Areas</FooterLink>
                <FooterLink href="/tools/site-audit">
                  Free Website Audit
                </FooterLink>
                <FooterLink href="/faq">FAQ</FooterLink>
                <FooterLink href="/blog">Blog</FooterLink>
                <FooterLink href="/contact">Contact</FooterLink>
              </ul>
            </div>

            {/* Newsletter */}
            <div>
              <h3 className="text-white font-bold text-lg mb-4">
                Stay Updated
              </h3>
              <p className="text-white/70 text-sm mb-3">
                Subscribe to our newsletter for the latest AI insights.
              </p>
              <form onSubmit={handleSubscribe} className="newsletter-form">
                <div className="flex">
                  <input
                    type="email"
                    placeholder="Your email"
                    className="input-field rounded-r-none"
                    value={email}
                    onChange={handleEmailChange}
                    onKeyDown={handleKeyDown}
                    disabled={isSubmitting}
                    aria-label="Email address for newsletter"
                    autoComplete="email"
                  />
                  <button
                    type="submit"
                    className="bg-elev8-blue hover:bg-elev8-blue/90 text-white font-medium rounded-r-lg px-4 transition-colors duration-300 flex items-center justify-center min-w-[42px] disabled:opacity-60"
                    disabled={isSubmitting || !shield.ready}
                    aria-label="Subscribe to newsletter"
                  >
                    {isSubmitting ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <ArrowUpRight size={18} />
                    )}
                  </button>
                </div>
                {/* Spam shield: hidden honeypot + invisible Turnstile,
                    both verified server-side in /api/newsletter/subscribe. */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                  {...shield.honeypot}
                />
                <TurnstileWidget
                  onVerify={shield.onTurnstileVerify}
                  onUnavailable={shield.onTurnstileUnavailable}
                  resetSignal={shield.resetSignal}
                  className="mt-3"
                />
                {renderFeedback()}
              </form>
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center">
            <p className="text-white/60 text-sm">
              © {currentYear} Elev8 Growth Solutions. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link
                to="/privacy-policy"
                className="text-white/60 text-sm hover:text-white transition-colors duration-300"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-service"
                className="text-white/60 text-sm hover:text-white transition-colors duration-300"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Modern gradient effects instead of BlurBubbles */}
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-elev8-blue/5 filter blur-[80px] opacity-20 pointer-events-none"></div>
      <div className="absolute bottom-[10%] right-[5%] w-[200px] h-[200px] rounded-full bg-elev8-blue/5 filter blur-[60px] opacity-15 pointer-events-none"></div>
    </footer>
  );
};

interface FooterLinkProps {
  href: string;
  children: React.ReactNode;
}

const FooterLink = memo(({ href, children }: FooterLinkProps) => (
  <li>
    <Link
      to={href}
      className="text-white/70 hover:text-elev8-blue transition-colors duration-300 flex items-center gap-1 group"
    >
      {children}
      <ArrowUpRight
        size={14}
        className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      />
    </Link>
  </li>
));
FooterLink.displayName = 'FooterLink';

interface SocialIconProps {
  icon: React.ReactNode;
  href: string;
  ariaLabel?: string;
}

const SocialIcon = memo(({ icon, href, ariaLabel }: SocialIconProps) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="w-8 h-8 rounded-full bg-white/5 hover:bg-elev8-blue/20 flex items-center justify-center text-white/80 hover:text-elev8-blue transition-all duration-300"
    aria-label={ariaLabel || 'Social Media'}
  >
    {icon}
  </a>
));
SocialIcon.displayName = 'SocialIcon';

export default memo(Footer);
